import { useState } from "react";
import axios from "axios";
import { Reveal, SectionHeading } from "@/components/motion";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  { name: "Per Event", price: "$0.04", unit: "/ event", desc: "Pay only for verified events. Ideal for pilots and seasonal networks." },
  { name: "Per Route", price: "$1.20", unit: "/ route / day", desc: "Full-day coverage for every event on a route, unlimited re-verification." },
  { name: "Per Fleet", price: "$390", unit: "/ fleet / mo", desc: "Flat monthly pricing per fleet with shared rule sets and pooled audit." },
  { name: "Per API Call", price: "$0.01", unit: "/ call", desc: "Raw engine access for platforms embedding verification into their own stack." },
  { name: "Enterprise", price: "Custom", unit: "SLA-backed", desc: "Dedicated infrastructure, custom geofence programs, insurer-grade export, 99.99% SLA." },
];

const EVENTS_PER_ROUTE_DAY = 25;
const ROUTES_PER_FLEET = 15;
const CALLS_PER_EVENT = 1.5;

const perEventCost = (events) => {
  let cost = 0;
  let remaining = events;
  const bands = [
    [100_000, 0.04],
    [400_000, 0.03],
    [Infinity, 0.02],
  ];
  for (const [cap, rate] of bands) {
    const take = Math.min(remaining, cap);
    cost += take * rate;
    remaining -= take;
    if (remaining <= 0) break;
  }
  return cost;
};

const fmt = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const quote = (events) => {
  const routes = events / EVENTS_PER_ROUTE_DAY / 30;
  return [
    { name: "Per Event", monthly: perEventCost(events), note: "volume-tiered: $0.04 → $0.02" },
    { name: "Per Route", monthly: routes * 1.2 * 30, note: `≈ ${Math.ceil(routes)} routes/day` },
    { name: "Per Fleet", monthly: (routes / ROUTES_PER_FLEET) * 390, note: `≈ ${Math.ceil(routes / ROUTES_PER_FLEET)} fleets` },
    { name: "Per API Call", monthly: events * CALLS_PER_EVENT * 0.01, note: `${CALLS_PER_EVENT} calls per event` },
  ];
};

const Pricing = () => {
  const [events, setEvents] = useState(250000);
  const quotes = quote(events);
  const best = quotes.reduce((a, b) => (a.monthly <= b.monthly ? a : b));

  return (
    <section data-testid="pricing" className="border-b border-border bg-muted/30">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <SectionHeading index="10" eyebrow="Pricing — Indicative" title="Truth, metered your way." />
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-5 border-t border-l border-border">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.06} className="border-b border-r border-border">
              <div
                data-testid={`pricing-${t.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`h-full p-8 flex flex-col ${i === 4 ? "bg-foreground text-background" : "hover:bg-muted/60 transition-colors duration-200"}`}
              >
                <p className={`font-mono text-[10px] tracking-[0.25em] uppercase ${i === 4 ? "text-background/60" : "text-muted-foreground"}`}>
                  {t.name}
                </p>
                <p className="font-display font-black tracking-tight text-4xl mt-6">{t.price}</p>
                <p className={`font-mono text-[10px] tracking-widest mt-1 ${i === 4 ? "text-background/60" : "text-muted-foreground"}`}>
                  {t.unit}
                </p>
                <p className={`text-sm leading-relaxed mt-6 flex-1 ${i === 4 ? "text-background/80" : "text-muted-foreground"}`}>
                  {t.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div data-testid="pricing-calculator" className="mt-8 border border-border bg-card">
            <div className="border-b border-border bg-muted/50 px-6 py-3 flex items-center justify-between flex-wrap gap-2">
              <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground">
                Estimate your monthly cost — live math, same metering the engine reports
              </p>
              <span className="font-mono text-[10px] tracking-widest text-telemetry uppercase">Calculator</span>
            </div>
            <div className="p-6 lg:p-8">
              <div className="flex flex-wrap items-end gap-6 mb-8">
                <div className="flex-1 min-w-[260px]">
                  <label htmlFor="events-input" className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground block mb-2">
                    Verified events per month
                  </label>
                  <input
                    id="events-input"
                    data-testid="events-input"
                    type="number"
                    min={1000}
                    step={1000}
                    value={events}
                    onChange={(e) => setEvents(Math.max(0, Number(e.target.value) || 0))}
                    className="w-full bg-background border border-border px-4 py-3 font-mono text-lg focus:outline-none focus:border-foreground transition-colors duration-200"
                  />
                </div>
                <input
                  data-testid="events-slider"
                  type="range"
                  min={10000}
                  max={2000000}
                  step={10000}
                  value={Math.min(events, 2000000)}
                  onChange={(e) => setEvents(Number(e.target.value))}
                  className="flex-[2] min-w-[260px] accent-[#0A0A0A] h-10"
                  aria-label="Events per month slider"
                />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
                {quotes.map((q) => {
                  const isBest = q.name === best.name && events > 0;
                  return (
                    <div
                      key={q.name}
                      data-testid={`quote-${q.name.toLowerCase().replace(/\s/g, "-")}`}
                      className={`border-b border-r border-border p-6 ${isBest ? "bg-foreground text-background" : ""}`}
                    >
                      <div className="flex items-center justify-between">
                        <p className={`font-mono text-[10px] tracking-[0.2em] uppercase ${isBest ? "text-background/60" : "text-muted-foreground"}`}>
                          {q.name}
                        </p>
                        {isBest && (
                          <span className="font-mono text-[9px] tracking-widest bg-truth text-white px-2 py-0.5">BEST FIT</span>
                        )}
                      </div>
                      <p className="font-display font-black tracking-tight text-3xl mt-4">
                        {events > 0 ? fmt(q.monthly) : "—"}
                      </p>
                      <p className={`font-mono text-[10px] tracking-wider mt-1 ${isBest ? "text-background/60" : "text-muted-foreground"}`}>
                        / month · {q.note}
                      </p>
                    </div>
                  );
                })}
              </div>
              {events > 1000000 && (
                <p data-testid="enterprise-note" className="font-mono text-[10px] tracking-wider text-telemetry mt-4 uppercase">
                  Above 1M events/month, Enterprise usually wins — talk to us.
                </p>
              )}
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div data-testid="pilot-cta" className="mt-8 border border-foreground bg-foreground text-background p-8 lg:p-10 flex flex-wrap items-center justify-between gap-6">
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-background/60 mb-2">
                Self-Serve · No Sales Call Required
              </p>
              <h3 className="font-display font-extrabold tracking-tight text-3xl">
                Start your pilot — $499 one-time.
              </h3>
              <p className="text-sm text-background/70 mt-2 max-w-lg">
                10,000 verified events included, your own API key issued instantly on payment, and
                the zone editor unlocked. Cancel the engagement anytime.
              </p>
            </div>
            <button
              data-testid="start-pilot-btn"
              onClick={async () => {
                const res = await axios.post(`${API}/payments/checkout`, {
                  package_id: "pilot",
                  origin_url: window.location.origin,
                });
                window.location.href = res.data.checkout_url;
              }}
              className="bg-background text-foreground font-mono text-xs tracking-[0.2em] uppercase px-8 py-4 hover:bg-truth hover:text-white transition-colors duration-200 flex items-center gap-2"
            >
              Start Pilot <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 font-mono text-[10px] tracking-widest text-muted-foreground uppercase">
            Placeholder pricing — final terms set during access review.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Pricing;
